import { useState, useRef, useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from './ui/alert-dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { AlertTriangle, Calendar } from 'lucide-react';

/**
 * Shared leave-override dialog.
 *
 * Props:
 *   open        — boolean
 *   onCancel    — () => void
 *   onOverride  — (reason: string) => void
 *   blocks      — array of { operator_name, message, leave: [{ leave_type_name, start_date, end_date }] }
 *                 OR a single detail object with { message, leave }
 */
export default function LeaveOverrideDialog({ open, onCancel, onOverride, blocks }) {
  const [reason, setReason] = useState('');
  const overridingRef = useRef(false);

  // Reset the ref when the dialog opens
  useEffect(() => {
    if (open) overridingRef.current = false;
  }, [open]);

  // Normalise to array
  const items = Array.isArray(blocks) ? blocks : blocks ? [blocks] : [];

  const handleOverride = () => {
    if (!reason.trim()) return;
    overridingRef.current = true;
    onOverride(reason.trim());
    setReason('');
  };

  const handleCancel = () => {
    if (overridingRef.current) return;
    setReason('');
    onCancel();
  };

  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) handleCancel(); }}>
      <AlertDialogContent className="max-w-lg" data-testid="leave-override-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-amber-600" data-testid="leave-override-title">
            <AlertTriangle className="h-5 w-5" />
            Worker on leave
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              {items.map((block, bi) => (
                <div key={bi} className="space-y-2">
                  {block.operator_name && (
                    <p className="text-sm font-medium text-foreground">{block.operator_name}</p>
                  )}
                  <p className="text-sm text-muted-foreground">{block.message}</p>
                  {block.leave?.length > 0 && (
                    <div className="space-y-1">
                      {block.leave.map((l, li) => (
                        <div key={li} className="flex items-center gap-2 text-xs bg-amber-500/10 border border-amber-500/20 rounded px-2 py-1">
                          <Calendar className="h-3 w-3 text-amber-500 shrink-0" />
                          <span className="text-amber-700 dark:text-amber-300 font-medium">{l.leave_type_name || l.leave_type || 'Leave'}</span>
                          <span className="text-muted-foreground">{l.start_date} — {l.end_date}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              <div className="space-y-2 pt-2 border-t">
                <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Reason for override <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Why is this allocation needed despite the leave?"
                  rows={2}
                  data-testid="leave-override-reason"
                  className="resize-none"
                />
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel} data-testid="leave-override-cancel">
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleOverride}
            disabled={!reason.trim()}
            className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
            data-testid="leave-override-confirm"
          >
            Allocate anyway
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
